import { zodResolver } from '@hookform/resolvers/zod';
import { useLocale } from 'next-intl';
import { type FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { LanguageSelectorSchema } from '@/core/schemas/language-selector-schema';
import useAuthSession from '@/core/hooks/use-auth-session';
import useTranslations from '@/core/hooks/use-translations';
import parseLocale from '@/core/i18n/parse-locale';
import { usePathname, useRouter } from '@/core/i18n/routing';
import { languageSelectorSchema } from '@/core/schemas/language-selector-schema';
import { trackInteraction } from '@/core/services/ga4/ga4-service';
import { clearBookingDraft } from '@/core/utils/booking-draft';
import { clearCheckoutMarketSession } from '@/core/utils/clear-checkout-market-session';
import buildTargetPathForLocale from '@/core/utils/locale/build-target-path-for-locale';
import composeLocale from '@/core/utils/locale/compose-locale';
import persistLanguageChangerSelection from '@/core/utils/locale/persist-language-changer-selection';
import Alert from '@/ui/components/Alert/Alert';
import Button from '@/ui/components/Button/Button';
import { Form } from '@/ui/components/Form/Form';
import GenericSelect from '@/ui/components/Form/GenericSelect';
import Typography from '@/ui/components/Typography/Typography';
import useCountryLanguageOptions from '@/ui/hooks/use-country-language-options';
import useSubmitHandler from '@/ui/hooks/use-submit-handler';

import $ from './LanguageChanger.module.scss';

type LanguageChangerFormProps = {
  onSubmit: () => void;
};

const LanguageChangerForm: FC<LanguageChangerFormProps> = ({ onSubmit }) => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const isAuthenticated = useAuthSession();

  const locale = useLocale();
  const { language, country: countryFromLocale } = parseLocale(locale);
  const country = countryFromLocale.toUpperCase();

  const {
    selectedCountry,
    selectedLanguage,
    setSelectedLanguage,
    countriesOptions,
    languagesOptions,
    handleSelectCountry,
    isBrowseWithoutOrderFlowCountry,
    selectedOrganization,
    evaCountryIds,
    hasSelectedAvailable,
  } = useCountryLanguageOptions({
    country,
    language,
  });

  const handleSubmit = async () => {
    trackInteraction({
      interaction_type: 'change country/language',
      click_target: 'Language selector',
      click_text: `${selectedLanguage}-${selectedCountry}`,
      content_name: 'language_selector_form',
    });

    const targetLocale = composeLocale(selectedLanguage, selectedCountry);
    if (!targetLocale) {
      return;
    }

    if (selectedCountry && selectedCountry.toUpperCase() !== country) {
      clearCheckoutMarketSession();
      clearBookingDraft();
    }

    persistLanguageChangerSelection({
      targetLocale,
      isAuthenticated,
      isBrowseWithoutOrderFlowCountry,
      organizationId: selectedOrganization?.organizationId,
    });

    const targetPath = await buildTargetPathForLocale({
      pathname,
      currentLocale: locale,
      targetLocale,
      evaCountryIds,
      search: window.location.search,
    });
    router.push(targetPath, { locale: targetLocale });
    onSubmit();
  };

  const { isLoading, alert, submit } =
    useSubmitHandler<LanguageSelectorSchema>(handleSubmit);
  const form = useForm<LanguageSelectorSchema>({
    resolver: zodResolver(languageSelectorSchema),
    defaultValues: {
      country: country || '',
      language: language || '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  // Set the form language value after the state update so languagesOptions
  // already reflects the new country when Radix Select renders.
  useEffect(() => {
    if (selectedLanguage) {
      form.setValue('language', selectedLanguage);
    }
  }, [selectedLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Form {...form}>
      <Typography variant="h4">
        {t('web_navbar_languageSelector_titleLanguageOnly')}
      </Typography>
      <form
        onSubmit={async e => {
          e.preventDefault();
          await form.handleSubmit(submit)(e);
        }}
        className={$.form}
      >
        <GenericSelect
          form={form}
          name="country"
          label={t('web_navbar_languageSelector_country')}
          options={countriesOptions}
          onChange={handleSelectCountry}
        />

        <GenericSelect
          form={form}
          name="language"
          label={t('web_navbar_languageSelector_language')}
          options={languagesOptions}
          defaultValue={language}
          onChange={setSelectedLanguage}
        />

        {alert?.message && <Alert message={alert.message} type={alert.type} />}

        <Button
          disabled={isLoading || !hasSelectedAvailable}
          fullWidth
          type="submit"
        >
          {isLoading
            ? t('web_general_label_loading')
            : t('web_general_button_continue')}
        </Button>
      </form>
    </Form>
  );
};

export default LanguageChangerForm;
